import { useQueryClient } from '@tanstack/vue-query';

import type { QueryClient } from '@tanstack/query-core';
import type { Maybe } from '@nova/core';

import { queryKeys } from '../queryKeys';

import type { SpotGridArea, SpotGridItem } from './spotGridTypes';

/**
 * On-demand, read-only lookups against the shared spot-grid TanStack query cache.
 *
 * The always-mounted board owner (`useYardSpotGridPresenterV2` in `mode: 'live'`) keeps the
 * `queryKeys.spotGrid(warehouseId)` cache fresh via its EventHub listeners. Lookup-only
 * consumers (e.g. per-card drag-and-drop helpers) only need to resolve a spot/area by id at
 * interaction time, so they can read that cache directly instead of mounting their own
 * presenter instance. Mounting a presenter per card registers duplicate EventHub listeners
 * and builds per-instance reactive index Maps of the entire grid — memory that scales with
 * the number of cards on the board.
 *
 * These functions iterate the cached areas on each call (O(areas + spots)); prefer calling
 * them lazily (on drop/hover), not inside render or reactive computeds.
 */
export function readYardSpotGridAreas(
  queryClient: QueryClient,
  warehouseId: Maybe<string>
): SpotGridArea[] {
  if (!warehouseId) {
    return [];
  }

  return queryClient.getQueryData<SpotGridArea[]>(queryKeys.spotGrid(warehouseId)) ?? [];
}

export function getYardSpotItemById(
  queryClient: QueryClient,
  warehouseId: Maybe<string>,
  spotId: Maybe<string>
): SpotGridItem | null {
  if (!spotId) {
    return null;
  }

  for (const area of readYardSpotGridAreas(queryClient, warehouseId)) {
    const spot = area.spots.find(item => item.id === spotId);
    if (spot) {
      return spot;
    }
  }

  return null;
}

export function getYardSpotAreaById(
  queryClient: QueryClient,
  warehouseId: Maybe<string>,
  areaId: Maybe<string>
): SpotGridArea | null {
  if (!areaId) {
    return null;
  }

  return readYardSpotGridAreas(queryClient, warehouseId).find(area => area.id === areaId) ?? null;
}

/**
 * Vue facade for {@link getYardSpotItemById} / {@link getYardSpotAreaById}.
 *
 * Wraps `useQueryClient` so consumers can resolve spots/areas from the shared cache without
 * mounting `useYardSpotGridPresenterV2` or threading `queryClient` through call sites.
 */
export function useYardSpotGridLookup() {
  const queryClient = useQueryClient();

  return {
    getSpotItemById: (warehouseId: Maybe<string>, spotId: Maybe<string>) =>
      getYardSpotItemById(queryClient, warehouseId, spotId),

    getAreaById: (warehouseId: Maybe<string>, areaId: Maybe<string>) =>
      getYardSpotAreaById(queryClient, warehouseId, areaId)
  };
}
